import React, { useEffect, useRef, useState, useMemo } from "react";
import {
  Upload,
  Sparkles,
  GitBranch,
  Database,
  X,
  Search,
  ClipboardCopy,
  Webhook,
  Cloud,
  FileText,
  CornerDownRight,
  Split,
  ChevronDown,
  ChevronRight,
  GripVertical,
  Repeat,
  Bot,
  Mail,
  FileSpreadsheet,
  Code,
  BrainCircuit,
  ShieldAlert,
  ShieldCheck,
  Send,
  Clock,
  Globe,
} from "lucide-react";
import { MdHttp } from "react-icons/md";

interface LeftSidebarProps {
  onAddNode: (type: string) => void;
  onClose: () => void;
  existingNodes?: string[];
}

export const LeftSidebar: React.FC<LeftSidebarProps> = ({
  onAddNode,
  onClose,
}) => {
  const sidebarRef = useRef<HTMLDivElement>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(["Triggers", "Actions", "Conditionals", "Exports"])
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  const toggleCategory = (category: string) => {
    setExpandedCategories((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(category)) {
        newSet.delete(category);
      } else {
        newSet.add(category);
      }
      return newSet;
    });
  };

  const nodeCategories = [
    {
      category: "Triggers",
      nodes: [
        {
          type: "document-upload",
          label: "On Document Upload",
          icon: Upload,
          color: "green",
          description: "Trigger on upload",
        },
        {
          type: "document-store",
          label: "DocIQ Trigger",
          icon: Database,
          color: "blue",
          description: "Trigger on storage",
        },
        {
          type: "http-request",
          label: "HTTP Trigger",
          icon: MdHttp,
          color: "amber",
          description: "Trigger on HTTP req",
        },
        {
          type: "one-drive",
          label: "OneDrive Trigger",
          icon: Cloud,
          color: "blue",
          description: "Trigger from OneDrive",
        },
        {
          type: "GMAIL_CONNECTOR",
          label: "Gmail Trigger",
          icon: Mail,
          color: "red",
          description: "Trigger from Gmail",
        },
        {
          type: "OUTLOOK-CONNECTOR",
          label: "Outlook Trigger",
          icon: Mail,
          color: "indigo",
          description: "Trigger from Outlook",
        },
        {
          type: "schedule",
          label: "Schedule",
          icon: Clock,
          color: "purple",
          description: "Run on a cron schedule",
        },
      ],
    },
    {
      category: "Actions",
      nodes: [
        {
          type: "summarize",
          label: "LLM node",
          icon: FileText,
          color: "purple",
          description: "AI processing/Summarize",
        },
        {
          type: "email-parsing",
          label: "Email Parsing",
          icon: Mail,
          color: "amber",
          description: "AI email processing",
        },

        {
          type: "ocr-processing",
          label: "OCR Extraction",
          icon: Sparkles,
          color: "purple",
          description: "Extract text/data",
        },
        {
          type: "cloud-vision",
          label: "Cloud OCR",
          icon: Cloud,
          color: "blue",
          description: "Google Cloud Vision OCR",
        },
        {
          type: "gemini-understanding",
          label: "Smart Extraction (Gemini)",
          icon: Bot,
          color: "violet",
          description: "Gemini AI Understanding",
        },
        {
          type: "SMART-PARSE",
          label: "Document Parsing",
          icon: BrainCircuit,
          color: "pink",
          description: "AI-powered data extraction",
        },
        {
          type: "compare-data",
          label: "Compare Data",
          icon: GitBranch,
          color: "cyan",
          description: "Compare data points",
        },
        {
          type: "transform",
          label: "Transform",
          icon: ClipboardCopy,
          color: "purple",
          description: "Transform data fields",
        },
        {
          type: "pdf-generator",
          label: "PDF Generator",
          icon: FileText,
          color: "orange",
          description: "Generate PDF docs",
        },
        {
          type: "http-api",
          label: "HTTP Request",
          icon: MdHttp,
          color: "indigo",
          description: "Call external APIs",
        },
        {
          type: "CODE",
          label: "Code Node",
          icon: Code,
          color: "slate",
          description: "Run JS/Python code",
        },
        {
          type: "error-catcher",
          label: "Error Catcher",
          icon: ShieldAlert,
          color: "red",
          description: "Catch node errors",
        },
        {
          type: "web-scraper",
          label: "Web Scraper",
          icon: Globe,
          color: "cyan",
          description: "Crawl & import web content",
        },
      ],
    },
    {
      category: "Conditionals",
      nodes: [
        {
          type: "if-else",
          label: "If-Else",
          icon: CornerDownRight,
          color: "indigo",
          description: "Branch workflow",
        },

        {
          type: "switch",
          label: "Switch",
          icon: Split,
          color: "indigo",
          description: "Multi-path routing",
        },
        {
          type: "for-each",
          label: "For Each",
          icon: Repeat,
          color: "cyan",
          description: "Loop over items",
        },
        {
          type: "validator",
          label: "Validator",
          icon: ShieldCheck,
          color: "amber",
          description: "Validate & verify extracted data",
        },
      ],
    },
    {
      category: "Exports",
      nodes: [
        {
          type: "mongodb",
          label: "MongoDB Connector",
          icon: Database,
          color: "green",
          description: "Save to MongoDB",
        },
        {
          type: "webhook",
          label: "Webhook Export",
          icon: Webhook,
          color: "amber",
          description: "Send to webhook",
        },
        {
          type: "EXCEL",
          label: "Export to Excel",
          icon: FileSpreadsheet,
          color: "green",
          description: "Create Excel reports",
        },
        {
          type: "WORD",
          label: "Export to Word",
          icon: FileText,
          color: "blue",
          description: "Create Word docs",
        },
        {
          type: "onedrive-upload",
          label: "OneDrive Upload",
          icon: Cloud,
          color: "blue",
          description: "Upload to OneDrive",
        },
        {
          type: "OUTLOOK-SEND",
          label: "Outlook Send",
          icon: Send,
          color: "blue",
          description: "Send email via Outlook",
        },
        {
          type: "GMAIL-SEND",
          label: "Gmail Send",
          icon: Send,
          color: "red",
          description: "Send email via Gmail",
        },
        {
          type: "docxiq-export",
          label: "DocIQ Export",
          icon: Database,
          color: "indigo",
          description: "Upload files to DocIQ store",
        },
        {
          type: "postgres",
          label: "PostgreSQL",
          icon: Database,
          color: "blue",
          description: "Insert/Update/Select/Delete rows",
        },
      ],
    },
  ];

  const colorClasses = {
    indigo: "bg-indigo-100 text-indigo-600",
    green: "bg-emerald-100 text-emerald-600",
    purple: "bg-purple-100 text-purple-600",
    orange: "bg-orange-100 text-orange-600",
    blue: "bg-blue-100 text-blue-600",
    violet: "bg-violet-100 text-violet-600",
    cyan: "bg-cyan-100 text-cyan-600",
    amber: "bg-amber-100 text-amber-600",
    red: "bg-red-100 text-red-600",
    pink: "bg-pink-100 text-pink-600",
  };

  const filteredCategories = useMemo(() => {
    if (!searchTerm.trim()) return nodeCategories;
    const lower = searchTerm.toLowerCase();

    return nodeCategories
      .map((cat) => ({
        ...cat,
        nodes: cat.nodes.filter(
          (node) =>
            node.label.toLowerCase().includes(lower) ||
            node.description.toLowerCase().includes(lower)
        ),
      }))
      .filter((cat) => cat.nodes.length > 0);
  }, [searchTerm]);

  return (
    <div
      ref={sidebarRef}
      className="w-64 bg-white h-[calc(85vh-2rem)] ml-2 mt-[80px] flex flex-col overflow-hidden border border-gray-200 rounded-lg shadow-sm animate-slideIn font-sans"
    >
      {/* Header */}
      <div className="p-3 border-b border-gray-100">
        <div className="relative">
          <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-2.5 pointer-events-none" />
          <input
            type="text"
            placeholder="Search nodes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-8 pr-7 py-1.5 text-xs bg-gray-50 border border-gray-200 rounded-md focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 focus:outline-none transition-all placeholder:text-gray-400"
          />
          {searchTerm && (
            <button
              type="button"
              onClick={() => setSearchTerm("")}
              className="absolute right-2 top-2 text-gray-400 hover:text-gray-600"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Node List */}
      <div className="flex-1 overflow-y-auto custom-scrollbar">
        {filteredCategories.length > 0 ? (
          filteredCategories.map((category) => (
            <div
              key={category.category}
              className="border-b border-gray-50 last:border-0"
            >
              <button
                onClick={() => toggleCategory(category.category)}
                className="w-full flex items-center justify-between px-3 py-2 bg-gray-50/50 hover:bg-gray-100 transition-colors group"
              >
                <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider group-hover:text-gray-700">
                  {category.category}
                </span>
                {expandedCategories.has(category.category) ? (
                  <ChevronDown className="w-3 h-3 text-gray-400 group-hover:text-gray-600" />
                ) : (
                  <ChevronRight className="w-3 h-3 text-gray-400 group-hover:text-gray-600" />
                )}
              </button>

              {expandedCategories.has(category.category) && (
                <div className="p-2 space-y-1">
                  {category.nodes.map((component) => {
                    const IconComponent = component.icon;
                    const colorClass =
                      colorClasses[
                      component.color as keyof typeof colorClasses
                      ] || "bg-gray-100 text-gray-600";

                    return (
                      <div
                        key={component.type}
                        className="group relative flex items-center gap-3 p-2 rounded-md hover:bg-indigo-50 border border-gray-200 hover:border-indigo-100 cursor-grab active:cursor-grabbing transition-all duration-200"
                        onClick={() => onAddNode(component.type)}
                        draggable
                        onDragStart={(e) => {
                          e.dataTransfer.setData(
                            "application/reactflow",
                            component.type
                          );
                          e.dataTransfer.effectAllowed = "move";
                        }}
                      >
                        {/* Drag Handle (Visible on Hover) */}
                        <div className="absolute left-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <GripVertical className="w-3 h-3 text-gray-300" />
                        </div>

                        <div
                          className={`p-1.5 rounded-md ${colorClass} group-hover:bg-white group-hover:shadow-sm transition-all ml-2`}
                        >
                          <IconComponent className="w-4 h-4" />
                        </div>

                        <div className="flex-1 min-w-0">
                          <h3 className="text-xs font-medium text-gray-700 group-hover:text-indigo-700 truncate">
                            {component.label}
                          </h3>
                          <p className="text-[10px] text-gray-400 group-hover:text-indigo-400 truncate">
                            {component.description}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="flex flex-col items-center justify-center pt-10 text-gray-400">
            <Search className="w-8 h-8 mb-2 opacity-20" />
            <p className="text-xs">No nodes found</p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="bg-gray-50 border-t border-gray-100 p-2">
        <p className="text-[10px] text-gray-400 text-center flex items-center justify-center gap-1">
          <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-pulse"></span>
          Drag nodes to canvas
        </p>
      </div>
    </div>
  );
};

export default LeftSidebar;
